import { useEffect } from "react";
import {
  Users, MessageCircle, ShoppingBag, TrendingUp,
  UserPlus, Megaphone, Package, Bot,
  Brain, Radio, Shield, Sparkles,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { motion } from "framer-motion";
import { cn } from "@/lib/cn";
import { useAuthStore } from "@/stores/authStore";
import { useDashboardStore } from "@/stores/dashboardStore";
import type { DashboardActivity } from "@/stores/dashboardStore";
import { useLanguageStore } from "@/stores/languageStore";

const stagger = {
  animate: { transition: { staggerChildren: 0.04 } },
};

const fadeUp = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.3, ease: "easeOut" as const } },
};

interface TaskCardProps {
  icon: LucideIcon;
  title: string;
  titleAm: string;
  description: string;
  descriptionAm: string;
  path: string;
  tone: string;
}

function getGreeting(): { en: string; am: string } {
  const hour = new Date().getHours();
  if (hour < 12) return { en: "Good morning", am: "እንደምን አደርክ" };
  if (hour < 17) return { en: "Good afternoon", am: "እንደምን ዋልክ" };
  return { en: "Good evening", am: "እንደምን አመሸህ" };
}

function localize(en: string, am: string, language: "en" | "am"): string {
  return language === "am" ? am : en;
}

interface StatCardProps {
  icon: LucideIcon;
  label: string;
  labelAm: string;
  value: string | number;
  iconBg: string;
  iconColor: string;
}

function StatCard({ icon: Icon, label, labelAm, value, iconBg, iconColor }: StatCardProps) {
  const language = useLanguageStore((state) => state.language);

  return (
    <motion.div variants={fadeUp} whileTap={{ scale: 0.97 }}
      className="rounded-2xl bg-[var(--color-surface)] p-3.5 shadow-sm"
    >
      <div className="flex items-center justify-between">
        <div className={cn("flex h-9 w-9 items-center justify-center rounded-xl", iconBg)}>
          <Icon className={cn("h-[18px] w-[18px]", iconColor)} />
        </div>
      </div>
      <div className="mt-3">
        <span className="font-headline text-xl font-bold text-[var(--color-text)]">{value}</span>
        <p className="mt-0.5 text-xs text-[var(--color-text-muted)]">
          {localize(label, labelAm, language)}
        </p>
      </div>
    </motion.div>
  );
}

interface QuickActionProps {
  icon: LucideIcon;
  label: string;
  labelAm: string;
  bg: string;
  color: string;
  onClick?: () => void;
}

function QuickAction({ icon: Icon, label, labelAm, bg, color, onClick }: QuickActionProps) {
  const language = useLanguageStore((state) => state.language);

  return (
    <motion.button variants={fadeUp} whileTap={{ scale: 0.93 }} onClick={onClick}
      className="flex flex-col items-center gap-2"
    >
      <div className={cn("flex h-12 w-12 items-center justify-center rounded-2xl", bg)}>
        <Icon className={cn("h-5 w-5", color)} />
      </div>
      <span className="text-center text-[11px] font-medium text-[var(--color-text-secondary)]">
        {localize(label, labelAm, language)}
      </span>
    </motion.button>
  );
}

const QUICK_ACTIONS: QuickActionProps[] = [
  { icon: UserPlus, label: "Contact", labelAm: "እውቂያ", bg: "bg-blue-50", color: "text-blue-600", onClick: () => { window.location.hash = "/contacts/add"; } },
  { icon: Megaphone, label: "Broadcast", labelAm: "ስርጭት", bg: "bg-amber-50", color: "text-amber-600", onClick: () => { window.location.hash = "/marketing/new-campaign"; } },
  { icon: Package, label: "Business", labelAm: "ንግድ", bg: "bg-emerald-50", color: "text-emerald-600", onClick: () => { window.location.hash = "/business"; } },
  { icon: Bot, label: "AI Brain", labelAm: "ኤአይ አእምሮ", bg: "bg-violet-50", color: "text-violet-600", onClick: () => { window.location.hash = "/intelligence"; } },
];

const OWNER_TASKS: TaskCardProps[] = [
  {
    icon: UserPlus,
    title: "Review contacts",
    titleAm: "እውቂያዎችን ይመልከቱ",
    description: "Add or group people as family, friend, business, VIP, or other.",
    descriptionAm: "ሰዎችን እንደ ቤተሰብ፣ ጓደኛ፣ ንግድ፣ VIP ወይም ሌላ ያደራጁ።",
    path: "/contacts",
    tone: "bg-blue-50 text-blue-600",
  },
  {
    icon: Shield,
    title: "Add bot to group",
    titleAm: "ቦቱን ወደ ቡድን ያክሉ",
    description: "Set welcome messages, spam protection, and moderation rules.",
    descriptionAm: "የእንኳን ደህና መጣችሁ መልዕክት፣ spam መከላከያ እና የቁጥጥር ህጎችን ያዘጋጁ።",
    path: "/groups",
    tone: "bg-emerald-50 text-emerald-600",
  },
  {
    icon: Radio,
    title: "Manage channels",
    titleAm: "ቻናሎችን ያስተዳድሩ",
    description: "Schedule posts, review templates, and track channel activity.",
    descriptionAm: "ፖስቶችን ያቅዱ፣ ቴምፕሌቶችን ይመልከቱ እና የቻናል እንቅስቃሴን ይከታተሉ።",
    path: "/channels",
    tone: "bg-indigo-50 text-indigo-600",
  },
  {
    icon: Megaphone,
    title: "Create campaign",
    titleAm: "ካምፔይን ይፍጠሩ",
    description: "Write bilingual marketing messages and choose an audience.",
    descriptionAm: "የግብይት መልዕክቶችን ይጻፉ እና ታዳሚዎችን ይምረጡ።",
    path: "/marketing/new-campaign",
    tone: "bg-rose-50 text-rose-600",
  },
  {
    icon: Brain,
    title: "Ask AI agent",
    titleAm: "AI ወኪልን ይጠይቁ",
    description: "Get growth, sales, support, and research recommendations.",
    descriptionAm: "የእድገት፣ ሽያጭ፣ ድጋፍ እና ጥናት ምክሮችን ያግኙ።",
    path: "/growth",
    tone: "bg-violet-50 text-violet-600",
  },
  {
    icon: Package,
    title: "Check orders",
    titleAm: "ትዕዛዞችን ይፈትሹ",
    description: "Review products, order status, and payment sync state.",
    descriptionAm: "ምርቶችን፣ የትዕዛዝ ሁኔታን እና የክፍያ ማመሳሰልን ይመልከቱ።",
    path: "/business",
    tone: "bg-amber-50 text-amber-600",
  },
];

function TaskCard({ icon: Icon, title, titleAm, description, descriptionAm, path, tone }: TaskCardProps) {
  const language = useLanguageStore((state) => state.language);

  return (
    <motion.button
      type="button"
      variants={fadeUp}
      whileTap={{ scale: 0.98 }}
      onClick={() => {
        window.location.hash = path;
      }}
      className="flex w-full gap-3 rounded-2xl bg-[var(--color-surface)] p-4 text-left shadow-sm"
    >
      <div className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-xl", tone)}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-bold text-[var(--color-text)]">
            {localize(title, titleAm, language)}
          </h3>
          <span className="text-xs font-semibold text-[var(--color-primary)]">
            {localize("Open", "ክፈት", language)}
          </span>
        </div>
        <p className="mt-1 text-xs leading-5 text-[var(--color-text-secondary)]">
          {localize(description, descriptionAm, language)}
        </p>
      </div>
    </motion.button>
  );
}

function formatETB(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return value.toLocaleString();
}

function formatActivityTime(createdAt: string): string {
  const diffMs = Date.now() - new Date(createdAt).getTime();
  const minutes = Math.max(0, Math.floor(diffMs / 60_000));
  if (minutes < 1) return "now";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

function getActivityIcon(type: DashboardActivity["type"]): {
  icon: LucideIcon;
  iconBg: string;
  iconColor: string;
} {
  if (type === "contact") {
    return { icon: Users, iconBg: "bg-blue-50", iconColor: "text-blue-600" };
  }
  if (type === "campaign") {
    return { icon: Megaphone, iconBg: "bg-rose-50", iconColor: "text-rose-600" };
  }
  if (type === "message") {
    return { icon: MessageCircle, iconBg: "bg-emerald-50", iconColor: "text-emerald-600" };
  }
  return { icon: ShoppingBag, iconBg: "bg-amber-50", iconColor: "text-amber-600" };
}

export default function HomePage() {
  const user = useAuthStore((s) => s.user);
  const language = useLanguageStore((state) => state.language);
  const { summary, isLoading, error, fetchSummary } = useDashboardStore();
  const firstName = user?.first_name ?? "there";
  const greeting = getGreeting();
  const metrics = summary?.metrics;
  const revenue = metrics?.revenue ?? 0;
  const stats: StatCardProps[] = [
    { icon: Users, label: "Contacts", labelAm: "እውቂያዎች", value: metrics?.contacts ?? 0, iconBg: "bg-blue-50", iconColor: "text-blue-600" },
    { icon: MessageCircle, label: "Active Chats", labelAm: "ንቁ ቻቶች", value: metrics?.activeChats ?? 0, iconBg: "bg-emerald-50", iconColor: "text-emerald-600" },
    { icon: ShoppingBag, label: "Orders", labelAm: "ትዕዛዞች", value: metrics?.orders ?? 0, iconBg: "bg-amber-50", iconColor: "text-amber-600" },
  ];

  useEffect(() => {
    void fetchSummary();
  }, [fetchSummary]);

  return (
    <motion.div className="flex flex-1 flex-col pb-24" variants={stagger} initial="initial" animate="animate">
      <motion.div variants={fadeUp} className="px-4 pb-2 pt-4">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-full bg-gradient-to-br from-blue-600 to-indigo-600">
            <span className="font-headline text-lg font-bold text-white">
              {firstName[0]?.toUpperCase()}
            </span>
          </div>
          <div>
            <h1 className="font-headline text-lg font-bold text-[var(--color-text)]">
              {localize(greeting.en, greeting.am, language)}, {firstName}
            </h1>
            <p className="text-xs text-[var(--color-text-muted)]">
              {localize("Manage today from one place", "ዛሬን ከአንድ ቦታ ያስተዳድሩ", language)}
            </p>
          </div>
        </div>
      </motion.div>

      {error && (
        <motion.div variants={fadeUp} className="px-4 pt-3">
          <div className="rounded-2xl bg-red-50 px-4 py-3 text-sm text-red-700">
            {localize("Dashboard could not load.", "ዳሽቦርዱ መጫን አልቻለም።", language)}
          </div>
        </motion.div>
      )}

      <motion.div variants={fadeUp} className="px-4 pt-5">
        <div className="mb-3 flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
            <Sparkles className="h-4 w-4" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-[var(--color-text-secondary)]">
              {localize("What do you want to do?", "ምን ማድረግ ይፈልጋሉ?", language)}
            </h2>
          </div>
        </div>
        <motion.div variants={stagger} className="flex flex-col gap-3">
          {OWNER_TASKS.map((task) => (
            <TaskCard key={task.title} {...task} />
          ))}
        </motion.div>
      </motion.div>

      <motion.div variants={fadeUp} className="px-4 pt-3">
        <div className="rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-700 p-5 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-white/70">
                {localize("Revenue", "ገቢ", language)}
              </p>
              <div className="mt-1 flex items-baseline gap-2">
                <span className="font-headline text-3xl font-bold text-white">
                  {isLoading && !summary ? "..." : formatETB(revenue)}
                </span>
                <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs font-semibold text-white">
                  {metrics?.pendingOrders ?? 0} pending
                </span>
              </div>
              <p className="mt-1 text-xs text-white/50">
                {localize("ETB this month", "በዚህ ወር ETB", language)}
              </p>
            </div>
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15">
              <TrendingUp className="h-6 w-6 text-white" />
            </div>
          </div>
        </div>
      </motion.div>

      <motion.div variants={fadeUp} className="px-4 pt-5">
        <h2 className="text-sm font-semibold text-[var(--color-text-secondary)]">
          {localize("Today's Stats", "የዛሬ ስታቲስቲክስ", language)}
        </h2>
      </motion.div>

      <motion.div variants={stagger} className="grid grid-cols-3 gap-3 px-4 pt-2">
        {stats.map((stat) => <StatCard key={stat.label} {...stat} />)}
      </motion.div>

      <motion.div variants={fadeUp} className="px-4 pt-6">
        <h2 className="text-sm font-semibold text-[var(--color-text-secondary)]">
          {localize("Quick Actions", "ፈጣን ድርጊቶች", language)}
        </h2>
        <motion.div variants={stagger} className="flex justify-between">
          {QUICK_ACTIONS.map((action) => <QuickAction key={action.label} {...action} />)}
        </motion.div>
      </motion.div>

      <motion.div variants={fadeUp} className="px-4 pt-6">
        <h2 className="text-sm font-semibold text-[var(--color-text-secondary)]">
          {localize("Recent Activity", "የቅርብ ጊዜ እንቅስቃሴ", language)}
        </h2>
        <div className="overflow-hidden rounded-2xl bg-[var(--color-surface)] shadow-sm">
          {summary?.recentActivity.length ? summary.recentActivity.map((item, index) => {
            const { icon: ActivityIcon, iconBg, iconColor } = getActivityIcon(item.type);
            return (
              <motion.div
                key={item.title}
                variants={fadeUp}
                className={cn(
                  "flex items-center gap-3 px-4 py-3",
                  summary && index < summary.recentActivity.length - 1 && "border-b border-[var(--color-border-subtle)]",
                )}
              >
                <div className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-xl", iconBg)}>
                  <ActivityIcon className={cn("h-4 w-4", iconColor)} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-[var(--color-text)]">
                    {localize(item.title, item.titleAm, language)}
                  </p>
                  <p className="truncate text-xs text-[var(--color-text-muted)]">
                    {localize(item.subtitle, item.subtitleAm, language)}
                  </p>
                </div>
                <span className="shrink-0 text-[10px] text-[var(--color-text-muted)]">
                  {formatActivityTime(item.createdAt)}
                </span>
              </motion.div>
            );
          }) : (
            <div className="px-4 py-6 text-sm text-[var(--color-text-muted)]">
              {localize(
                "No activity yet. Add contacts, create orders, or start a campaign.",
                "እስካሁን እንቅስቃሴ የለም፤ እውቂያ ያክሉ፣ ትዕዛዝ ይፍጠሩ ወይም ካምፔይን ይጀምሩ።",
                language,
              )}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
